import React, { useRef } from "react";

import { ReactComponent as BustleTick } from "@images/bustle_tick_icon.svg";
import classNames from "classnames";
import { keyBy } from "lodash";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom-v5-compat";

import {
  Button,
  LinkButton,
  Price,
  ResponsivePrimaryAction,
  ResponsiveTitleBar,
} from "../Atoms";
import { useStartTabCheckoutSession } from "../Checkout/TablePayPayment";
import { bustleBeeForCustomer } from "../Customer/bustleBees";
import { useLoadedCustomer } from "../Customer/queries";
import { Customer } from "../Customer/schema";
import { useLoadedCustomerTab } from "../CustomerTab/queries";
import { OrderItem } from "../CustomerTab/schema";
import {
  useCreateOrderItemClaim,
  useDeleteOrderItemClaim,
} from "./OrderItemClaims/queries";
import { Pill } from "./Pill";
import { CustomerPillList } from "./Pill";
import { useLoadedBillSplit } from "./queries";
import { BillSplit, OrderItemClaim } from "./schema";
import {
  getClaimedOrderItemsIn,
  getOrderItemsTotal,
  hasCustomerPaidIn,
} from "./selectors";
import { getPaidAtTillAmount } from "./selectors";

export const MissingCustomer: Customer = {
  uuid: "missing",
  nickname: "Missing Customer",
  createdAt: "",
  icon: null,
};
type OrderItemStatus = "yours" | "others" | "unclaimed" | "paid_at_till";
export const OrderItemsClaim = ({}: {}) => {
  const { t } = useTranslation();
  const { data: customerTab } = useLoadedCustomerTab();
  const { data: billSplit } = useLoadedBillSplit();
  const { data: customer } = useLoadedCustomer();

  const createCreateOrderItemClaim = useCreateOrderItemClaim(billSplit);
  const deleteOrderItemClaimMutation = useDeleteOrderItemClaim(billSplit);
  const claimItem = (orderItemUuid: string) => async () => {
    createCreateOrderItemClaim.mutate(orderItemUuid);
  };

  const deleteOrderItemClaim = (orderItemClaim: OrderItemClaim) => async () => {
    deleteOrderItemClaimMutation.mutate(orderItemClaim);
  };

  const { startTabCheckoutSession, submissionState: checkoutSubmissionState } =
    useStartTabCheckoutSession();

  const orderItems = customerTab?.orders.flatMap((o) => o.orderItems);
  const orderItemClaims = keyBy(billSplit.orderItemClaims, "orderItemId");
  const customers = keyBy(billSplit.customers, "uuid");
  const getClaimedOrderItemsFor = getClaimedOrderItemsIn(
    billSplit,
    customerTab
  );
  const yourOrderItems = getClaimedOrderItemsFor(customer);
  const claimWithCustomer = (claim?: OrderItemClaim) => {
    if (!claim) {
      return undefined;
    }
    return {
      ...claim,
      customer: customers[claim.customerId] || MissingCustomer,
    };
  };

  const getHasCustomerPaid = hasCustomerPaidIn(billSplit);
  const yourOrderItemsTotal = getOrderItemsTotal(yourOrderItems);
  const hasPaid = getHasCustomerPaid(customer);
  const getOrderItemStatus = (orderItem: OrderItem): OrderItemStatus => {
    if (orderItemClaims[orderItem.uuid]?.customerId === customer.uuid) {
      return "yours";
    }
    if (orderItemClaims[orderItem.uuid]?.customerId) {
      return "others";
    }
    if (orderItem.paymentUuid) {
      return "paid_at_till";
    }
    return "unclaimed";
  };

  const paidAtTillAmount = getPaidAtTillAmount(billSplit, customerTab);

  return (
    <div className="space-y-32 max-w-screen-sm mx-auto pb-32">
      <ResponsiveTitleBar>
        {t("billSplit.selectYourItemsTitle")}
      </ResponsiveTitleBar>
      {hasPaid && (
        <div className="border-venue-highlight border-2 bg-venue-tertiary rounded-venue-brand p-12 ">
          <div className="font-semibold">{t("billSplit.youHavePaid")}</div>
          <div className="">{t("billSplit.youHavePaidDescription")}</div>
        </div>
      )}
      <div className="space-y-24">
        <div className="">
          <h3 className="text-xl font-bold">
            {t("billSplit.customersOnBillSplit")}
          </h3>
          <div>{t("billSplit.customersOnBillSplitDescription")}</div>
        </div>
        <CustomerPillList
          billSplit={billSplit}
          customerTab={customerTab}
          currentCustomer={customer}
        />
        <div className="flex items-center justify-between">
          <Link to="share">
            <Pill>{t("billSplit.shareWithOthers")} </Pill>
          </Link>
        </div>
        {paidAtTillAmount > 0 && (
          <h3 className="font-bold text-xl">
            {t("billSplit.paidAtTillAmount")}{" "}
            <Price showCurrency value={paidAtTillAmount} />
          </h3>
        )}
      </div>
      <div className="space-y-8">
        {orderItems.map((orderItem) => (
          <OrderItemSwitch
            key={orderItem.uuid}
            orderItem={orderItem}
            status={getOrderItemStatus(orderItem)}
            disabled={hasPaid}
            billSplit={billSplit}
            claim={claimWithCustomer(orderItemClaims[orderItem.uuid])}
            deleteOrderItemClaim={deleteOrderItemClaim(
              orderItemClaims[orderItem.uuid]
            )}
            claimItem={claimItem(orderItem.uuid)}
          />
        ))}
      </div>
      <div className="italic text-center">
        {t("billSplit.anyIssuesPayAtTheTill")}
      </div>
      <Button secondary onClick={startTabCheckoutSession}>
        {t("billSplit.payItAll")}
      </Button>

      <ResponsivePrimaryAction>
        {!hasPaid ? (
          <LinkButton primary to={`./review`}>
            {t("billSplit.readyForPayment")}{" "}
            <Price showCurrency value={yourOrderItemsTotal} />
          </LinkButton>
        ) : (
          <Button secondary disabled>
            {t("billSplit.youHavePaid")}
          </Button>
        )}
      </ResponsivePrimaryAction>
    </div>
  );
};

const OrderItemSwitch = ({
  orderItem,
  status,
  disabled,
  billSplit,
  deleteOrderItemClaim,
  claim,
  claimItem,
}: {
  orderItem: OrderItem;
  claim?: OrderItemClaim & { customer: Customer };
  status: OrderItemStatus;
  claimItem: () => void;
  billSplit: BillSplit;
  deleteOrderItemClaim: () => void;
  disabled?: boolean;
}) => {
  switch (status) {
    case "yours":
      return (
        <YourClaimedOrderItem
          orderItem={orderItem}
          disabled={disabled}
          deleteOrderItemClaim={deleteOrderItemClaim}
          billSplit={billSplit}
        />
      );

    case "others":
      return claim ? (
        <TheirClaimedOrderItem
          billSplit={billSplit}
          orderItem={orderItem}
          claim={claim}
          disabled={disabled}
        />
      ) : null;
    case "paid_at_till":
      return <PaidAtTillOrderItem orderItem={orderItem} />;
    case "unclaimed":
      return (
        <UnclaimedOrderItem
          orderItem={orderItem}
          disabled={disabled}
          claimItem={claimItem}
        />
      );
  }
};
export const ClaimableOrderItemContent = ({
  orderItem,
}: {
  orderItem: OrderItem;
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-12 ">
      {orderItem.itemHeroImageThumbnailUrl && (
        <div className="flex-none">
          <div className="  flex-none aspect-w-1 aspect-h-1 w-[80px] ">
            <img
              src={orderItem.itemHeroImageThumbnailUrl}
              className="rounded-venue-brand object-cover"
            />
          </div>
        </div>
      )}
      <div className="">
        <div className="font-bold">
          {t("billSplit.itemNameWithMultiplier", {
            itemName: orderItem.itemName,
            count: orderItem.multiplier,
          })}
        </div>
        <div>
          <Price value={orderItem.totalSalesPriceTaxInclusive} />
        </div>
        <ul className="text-xs font-normal list-disc">
          {orderItem.orderItemMods.map((mod) => mod.name).join(", ")}
        </ul>
      </div>
    </div>
  );
};

export const OrderItemBox = ({
  children,
  onClick,
  disabled,
  ourClaim,
  theirClaim,
}: {
  children: React.ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  ourClaim?: boolean;
  theirClaim?: boolean;
}) => {
  const unclaimed = !ourClaim && !theirClaim && !disabled;
  return (
    <div
      onClick={!disabled && !theirClaim && onClick ? onClick : undefined}
      className={classNames(
        "border-2 flex shadow-venue-brand-style rounded-venue-brand p-12 flex-wrap text-base font-medium items-center justify-between gap-8",
        {
          "cursor-pointer hover:border-venue-primary bg-venue-primary border-venue-primary text-venue-tertiary":
            ourClaim,
          "bg-venue-tertiary border-venue-tertiary ": !disabled && !ourClaim,
          "bg-venue-tertiary border-venue-tertiary grayscale brightness-[90%] ":
            theirClaim,
          "cursor-pointer": unclaimed,
        }
      )}
    >
      {children}
    </div>
  );
};

export const UnclaimedOrderItem = ({
  orderItem,
  claimItem,
  disabled,
}: {
  orderItem: OrderItem;
  claimItem: () => void;
  disabled?: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <OrderItemBox onClick={claimItem} disabled={disabled}>
      <ClaimableOrderItemContent orderItem={orderItem} />
    </OrderItemBox>
  );
};

const YourClaimedOrderItem = ({
  orderItem,
  deleteOrderItemClaim,
  billSplit,
  disabled,
}: {
  orderItem: OrderItem;
  billSplit: BillSplit;
  deleteOrderItemClaim: () => void;
  disabled?: boolean;
}) => {
  const { data: customer } = useLoadedCustomer();
  const customerSession = billSplit.customerSessions.find(
    (cs) => cs.customerId === customer.uuid
  );
  return (
    <OrderItemBox ourClaim disabled={disabled} onClick={deleteOrderItemClaim}>
      <ClaimableOrderItemContent orderItem={orderItem} />
      <div>
        <div className="text-venue-tertiary w-32 h-32">
          <BustleTick />
        </div>
      </div>
    </OrderItemBox>
  );
};
const PaidAtTillOrderItem = ({ orderItem }: { orderItem: OrderItem }) => {
  const { t } = useTranslation();
  return (
    <OrderItemBox theirClaim disabled>
      <ClaimableOrderItemContent orderItem={orderItem} />
      <div className=""> {t("billSplit.paidAtTill")}</div>
    </OrderItemBox>
  );
};
const TheirClaimedOrderItem = ({
  billSplit,
  orderItem,
  disabled,
  claim,
}: {
  billSplit: BillSplit;
  orderItem: OrderItem;
  disabled?: boolean;
  claim: OrderItemClaim & { customer: Customer };
}) => {
  const bustleBee = bustleBeeForCustomer(claim.customer);
  return (
    <OrderItemBox theirClaim disabled>
      <ClaimableOrderItemContent orderItem={orderItem} />
      {bustleBee && (
        <div className="rounded-full bg-white w-32 h-32">
          <img src={bustleBee.img_96} alt={bustleBee.alt} />
        </div>
      )}
    </OrderItemBox>
  );
};
export const BillSplitSelectOrderItems = ({}) => {
  const { t } = useTranslation();
  return (
    <div>
      <OrderItemsClaim />
    </div>
  );
};
export { useCreateOrderItemClaim };
